import { DimensionUnitType } from "../models/UserProfile";
export enum ArtworkMeasurementType {
    Artwork = 1,
    Framed = 2
}

export interface DimensionsPreviewStruct {
    label: string;
    value: React.ReactNode;
}
export interface ArtworkMeasurement {
    artworkMeasurementId?: number | null;
    measurementType: ArtworkMeasurementType;

    height: number | "";
    width: number | "";
    depth: number | "";
    dimensionUnit: string;

    weight: number | "";
    weightUnit: string;
}

export const getMeasurement = (
    measurements: ArtworkMeasurement[],
    type: ArtworkMeasurementType
): ArtworkMeasurement | undefined =>
    measurements.find(m => m.measurementType === type);

const convertDimension = (
    value: number,
    fromUnit: string | null | undefined,
    preference: DimensionUnitType
): number => {

    let centimetres: number;

    switch (fromUnit?.toLowerCase()) {
        case "m":
            centimetres = value * 100;
            break;

        case "in":
            centimetres = value * 2.54;
            break;

        case "cm":
        default:
            centimetres = value;
            break;
    }

    if (preference === DimensionUnitType.Imperial) {
        return centimetres / 2.54;
    }

    return centimetres;
};

interface ConvertedWeight {
    value: number;
    unit: string;
}

const convertWeight = (
    value: number,
    fromUnit: string | null | undefined,
    preference: DimensionUnitType
): ConvertedWeight => {

    let kilograms: number;

    switch (fromUnit?.toLowerCase()) {
        case "g":
        case "grams":
            kilograms = value / 1000;
            break;

        case "lbs":
        case "lb":
            kilograms = value * 0.45359237;
            break;

        case "kg":
        default:
            kilograms = value;
            break;
    }

    if (preference === DimensionUnitType.Imperial) {
        return {
            value: kilograms / 0.45359237,
            unit: "lbs"
        };
    }

    // Use grams for small metric weights
    if (kilograms < 1) {
        return {
            value: kilograms * 1000,
            unit: "g"
        };
    }

    return {
        value: kilograms,
        unit: "kg"
    };
};

const formatNumber = (value: number): string => {
    return Number(value.toFixed(2)).toString();
};

const hasValue = (
    value: number | string | null | undefined
): boolean =>
    value !== null &&
    value !== undefined &&
    value !== "";

export const formatMeasurement = (
    measurement?: ArtworkMeasurement,
    unitPreference: DimensionUnitType = DimensionUnitType.Metric
): string => {
    if (!measurement) return "";

    const {
        height,
        width,
        depth,
        dimensionUnit,
        weight,
        weightUnit
    } = measurement;

    const dimensions = [height, width, depth]
        .filter(hasValue)
        .map(Number);

    let dimString = "";

    if (dimensions.length > 0) {
        const convertedDimensions = dimensions.map(value =>
            convertDimension(
                value,
                dimensionUnit,
                unitPreference
            )
        );

        const displayUnit =
            unitPreference === DimensionUnitType.Imperial
                ? "in"
                : "cm";

        dimString =
            `${convertedDimensions.map(formatNumber).join(" × ")} ${displayUnit}`;
    }

    let weightString = "";

    if (hasValue(weight)) {
        const convertedWeight = convertWeight(
            Number(weight),
            weightUnit,
            unitPreference
        );

        if (convertedWeight) {
            weightString =
                `${formatNumber(convertedWeight.value)} ${convertedWeight.unit}`;
        }
    }

    return [dimString, weightString]
        .filter(Boolean)
        .join(" | ");
};